import { Component, h, Element, Prop, Build, Host } from '@stencil/core';
import {
  TRUSTED_AUTH_ORIGINS,
  getAuthUrl,
  getCookie,
  setCookie,
  removeCookie,
  getDomain,
  generateXSRFToken,
  openRegistrationPopup,
  analytics,
} from '../shared/index';
import { GoogleIcon } from '../shared/google-icon';

// Desktop-only, same threshold as type-1.
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 960;
};

/**
 * Google sign-up button for SignNow **forms pages**.
 *
 * Same button as `sn-social-auth-type-1`, with the forms-page flow from the legacy
 * `modules/social-auth/auth-buttons/index.js` (the branch taken when a registration modal exists
 * on the page): instead of navigating the tab to the provider link, it opens that link in a
 * 600×800 popup window, waits for the OAuth callback to `postMessage` back `{ code, provider }`,
 * exchanges it for a session, and then opens `sn-popup-type-1` — the user stays on the form page.
 *
 * Requires `sn-popup-type-1` to be present on the same page.
 *
 * Unlike the legacy module, the `message` listener verifies `event.origin` against
 * `TRUSTED_AUTH_ORIGINS`; the original accepted a `code` from any window that could reach it.
 *
 * @product signnow
 * @source social-auth
 * @version 1.0.0
 * @category SignNow
 * @example
 *   <sn-social-auth-type-2 button-text="Sign up with Google"></sn-social-auth-type-2>
 */
@Component({
  tag: 'sn-social-auth-type-2',
  styleUrl: '../type-1/index.scss',
  shadow: false,
})
export class SNSocialAuthType2 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Button label text.
   * @content
   * @dynamic
   * @default "Sign up with Google"
   */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Sign up with Google';

  /**
   * Form id handed to `sn-popup-type-1` so its "Back to form editing" action can resolve the
   * editor URL. When empty it is read from a `[data-formid]` element on the page.
   */
  @Prop({ attribute: 'form-id' }) formId?: string;

  private buttonElement?: HTMLElement;
  private boundHandleClick?: EventListener;
  private boundHandlePageShow?: EventListener;
  private messageHandler?: (event: MessageEvent) => void;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    if (isMobile()) return;

    this.buttonElement = this.el.querySelector('.js-sign-button') as HTMLElement;
    this.boundHandleClick = this.handleButtonClick.bind(this) as EventListener;
    this.buttonElement?.addEventListener('click', this.boundHandleClick);

    // Returning from the OAuth window can restore this page from bfcache with `is-hidden` still
    // applied — drop it so the button is usable again.
    this.boundHandlePageShow = (e: Event) => {
      if ((e as PageTransitionEvent).persisted) {
        this.el.querySelector('.js-sign-button')?.classList.remove('is-hidden');
      }
    };
    window.addEventListener('pageshow', this.boundHandlePageShow);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    if (this.buttonElement && this.boundHandleClick) {
      this.buttonElement.removeEventListener('click', this.boundHandleClick);
    }
    if (this.boundHandlePageShow) {
      window.removeEventListener('pageshow', this.boundHandlePageShow);
    }
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = undefined;
    }
  }

  private async handleButtonClick(e: Event) {
    e.preventDefault();

    if (!getCookie('social_auth_origin')) {
      setCookie('social_auth_origin', 'landing', { domain: getDomain(), path: '/' });
    }

    removeCookie('XSRF-TOKEN', { domain: getDomain(), path: '/' });
    analytics.freeTrialAction('Google', 'Start');

    const state = getCookie('SN_SOCIAL_AUTH_XSRF_TOKEN') || generateXSRFToken();

    try {
      const response = await fetch(`${getAuthUrl('/auth/v1')}/socialite/google/link?state=${state}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      this.hideGoogleButton();
      this.listenForAuthCode();
      window.open(data.link, '_blank', 'width=600,height=800');
    } catch (error) {
      console.error('Social auth error:', error);
    }
  }

  /**
   * Waits for the OAuth callback window to post `{ code, provider }`, then exchanges it for a
   * session and opens the registration popup. Single-shot: the listener removes itself.
   */
  private listenForAuthCode() {
    if (this.messageHandler) window.removeEventListener('message', this.messageHandler);

    this.messageHandler = async (event: MessageEvent) => {
      if (!TRUSTED_AUTH_ORIGINS.includes(event.origin)) return;

      const { code, provider } = (event.data || {}) as { code?: string; provider?: string };
      if (!code || !provider) return;

      window.removeEventListener('message', this.messageHandler!);
      this.messageHandler = undefined;

      try {
        const res = await fetch(getAuthUrl('/auth/v1/sessions/social'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code, provider, login_source: 'google_login_landing' }),
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        // No DataDog metric here on purpose: the legacy `auth-buttons` module tracked the button
        // flow only through GA events, and `v2/metrics/{name}` silently drops names missing from
        // its allowlist (200 with `success:false`), so inventing one would look wired up but
        // record nothing. Add the name to `allowDataDogMetricsName` in SignNowMain first.
        openRegistrationPopup(this.formId);
      } catch (error) {
        console.error('Error exchanging social auth code:', error);
      }
    };

    window.addEventListener('message', this.messageHandler, false);
  }

  private hideGoogleButton() {
    this.el.querySelector('.js-sign-button')?.classList.add('is-hidden');
  }

  render() {
    // Requires browser APIs, and the button is desktop-only.
    if (!Build.isBrowser || isMobile()) {
      return <Host data-color-mode={this.colorMode}></Host>;
    }

    return (
      <Host data-color-mode={this.colorMode}>
        <div class="google-button js-sign-button">
          <GoogleIcon />
          <div class="google-button__text">{this.buttonText}</div>
        </div>
      </Host>
    );
  }
}
